import { isApiRequestError } from "./base";

/**
 * Structural stand-ins for the Sentry SDK types. `@cede/utils` is consumed by
 * browser, Next server and Node builds that pin different Sentry packages, so
 * depending on `@sentry/types` here would force a version on all of them.
 */
type SentryEventLike = {
  fingerprint?: string[];
  // `unknown` values, not a narrow primitive union: Sentry's own `Primitive`
  // includes null/symbol/bigint, and a narrower type here rejects a real event.
  tags?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
};

type SentryEventHintLike = {
  originalException?: unknown;
};

/** Sentry rejects tag values over 200 chars; every value here is far shorter, but clamp anyway. */
const MAX_TAG_LENGTH = 200;

/**
 * Give an {@link ApiRequestError} a per-endpoint fingerprint plus indexed tags.
 *
 * Without this every transport failure across the app collapses into ONE Sentry
 * issue: the stack is always `ApiHelper._request`, and Sentry's default grouping
 * is stack-driven, so varying the message does not split the group. The error's
 * own properties are not captured either — that needs `extraErrorDataIntegration`,
 * which neither app installs — so endpoint/method/timeout are attached here.
 *
 * Only the `API_ENDPOINT` enum value is used, never the request URL.
 */
export function applyApiRequestErrorGrouping<TEvent extends SentryEventLike>(
  event: TEvent,
  hint?: SentryEventHintLike,
): TEvent {
  const error = hint?.originalException;
  if (!isApiRequestError(error)) {
    return event;
  }

  // Widened alias: the caller's concrete event type may declare narrower
  // `tags`/`contexts` than what is written here.
  const target = event as SentryEventLike;
  target.fingerprint = ["api-request", error.method, error.endpoint, error.isTimeout ? "timeout" : "transport"];
  target.tags = {
    ...target.tags,
    "api.endpoint": error.endpoint.slice(0, MAX_TAG_LENGTH),
    "api.method": error.method,
    "api.timeout": error.isTimeout,
  };
  target.contexts = {
    ...target.contexts,
    api_request: {
      endpoint: error.endpoint,
      method: error.method,
      isTimeout: error.isTimeout,
      correlationId: error.correlationId ?? null,
      originalErrorCode: error.originalErrorCode ?? null,
    },
  };

  return event;
}
